import { memo, useCallback, useEffect, useState } from 'react'
import { createTeam, deleteTeam, editTeam, getTeamById, getTeams } from 'services'
import { Team } from 'types/teams'
import { TeamsTable } from '../../components/teams-table'
import { Modal, Button } from 'antd'
import { TeamForm } from '../../components/team-form'
import styles from './index.module.scss'
import { recalculateTeamRating } from 'services/teams-ratings'

export const Teams = memo(() => {
    const [teams, setTeams] = useState<Team[]>([])
    const [open, setOpen] = useState<boolean>(false)
    const [team, setTeam] = useState<Team>({} as Team)
    const [loading, setLoading] = useState<boolean>(false)
    const [dataIsFetching, setDataIsFetching] = useState<boolean>(false)

    const getAllData = useCallback(() => {
        setDataIsFetching(true)

        const getAllTeams = async function () {
            return await getTeams()
        }

        getAllTeams()
            .then(teams => {
                setTeams(teams)
            })
            .finally(() => {
                setDataIsFetching(false)
            })
    }, [])

    useEffect(() => {
        getAllData()
    }, [getAllData])

    const toggleModal = useCallback(() => {
        setOpen(!open)
    }, [open])

    const handleAdd = useCallback(() => {
        setTeam({} as Team)
        toggleModal()
    }, [toggleModal])

    const handleRecalculateRatings = useCallback(() => {
        setLoading(true)

        recalculateTeamRating().then(() => {
            setLoading(false)
            getAllData()
        })
    }, [getAllData])

    const handleEdit = useCallback(
        (id: number) => {
            setLoading(true)
            getTeamById(id)
                .then(item => {
                    setTeam(item)
                    toggleModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleModal]
    )

    const handleDelete = useCallback(
        (id: number) => {
            setLoading(true)
            deleteTeam(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleFormSubmit = useCallback(
        (submitValues: Team) => {
            setLoading(true)

            const refetchDataAndCloseModal = () => {
                getAllData()
                toggleModal()
            }

            if (team.id) {
                editTeam({
                    ...submitValues,
                    id: team.id,
                })
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            } else {
                createTeam(submitValues)
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            }
        },
        [getAllData, team, toggleModal]
    )

    const editMode = !!team.id
    const modalTitle = `${editMode ? `Edit team ${team.name}` : `Add new team`}`

    const somethingLoading = loading || dataIsFetching

    return (
        <div>
            Teams
            <div className={styles.actions}>
                <Button type={'primary'} onClick={handleAdd}>
                    Add
                </Button>
                <Button type={'dashed'} onClick={handleRecalculateRatings}>
                    Recalculate ratings
                </Button>
            </div>
            <TeamsTable data={teams} onEdit={handleEdit} onDelete={handleDelete} loading={somethingLoading} />
            <Modal
                title={modalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <TeamForm position={team} onSubmit={handleFormSubmit} loading={somethingLoading} />
            </Modal>
        </div>
    )
})
